<template>
	<div>
		<div class="t-page-header">
			<div>
				<h1>שלום {{ user?.name?.split(" ")[0] }} 👋</h1>
				<div class="t-subtitle">סקירה כללית של המשימות והפרויקטים שלך</div>
			</div>
			<div style="display:flex; gap:8px;">
				<router-link to="/tasks/projects" class="t-btn ghost">פרויקטים</router-link>
				<button class="t-btn primary" @click="newTaskOpen = true">+ משימה חדשה</button>
			</div>
		</div>

		<div class="t-stats">
			<div class="t-stat">
				<div class="t-stat-label">משימות פתוחות</div>
				<div class="t-stat-value">{{ stats.my_tasks || 0 }}</div>
			</div>
			<div class="t-stat success">
				<div class="t-stat-label">משימות שהושלמו</div>
				<div class="t-stat-value">{{ stats.my_done || 0 }}</div>
			</div>
			<div class="t-stat danger">
				<div class="t-stat-label">חורגות מהדדליין</div>
				<div class="t-stat-value">{{ stats.overdue || 0 }}</div>
			</div>
			<div class="t-stat warn">
				<div class="t-stat-label">פרויקטים פעילים</div>
				<div class="t-stat-value">{{ stats.projects_count || 0 }}</div>
			</div>
		</div>

		<div class="t-dash-grid">
			<div>
				<div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
					<h3 style="margin:0; font-size:18px;">המשימות שלי</h3>
					<router-link to="/tasks/mine" class="t-btn link">לתצוגת רשימה ←</router-link>
				</div>

				<div v-if="!myTasks.length" class="t-empty">
					<div class="t-empty-icon">🎉</div>
					אין לך משימות פתוחות
				</div>

				<div v-else class="t-board t-board-dash">
					<div v-for="col in columns" :key="col.key" :class="['t-column','col-' + col.key]" @dragover.prevent @drop="dropTo(col.key, $event)">
						<div class="t-col-head">
							<span>{{ col.label }}</span>
							<span class="t-count">{{ grouped[col.key]?.length || 0 }}</span>
						</div>
						<div
							v-for="t in (grouped[col.key] || [])"
							:key="t.id"
							class="t-task-card"
							draggable="true"
							@dragstart="dragStart(t, $event)"
							@click="openTask(t.id)"
						>
							<button
								class="t-check on-card"
								:class="{ done: t.status === 'done' }"
								draggable="false"
								:title="t.status === 'done' ? 'בטל סימון הושלם' : 'סמן כהושלם'"
								@click.stop="toggleDone(t)"
								@mousedown.stop
							>✓</button>
							<span class="t-priority" :class="'p-' + t.priority">{{ priorityLabel(t.priority) }}</span>
							<h4>{{ t.title }}</h4>
							<div class="t-card-foot">
								<span class="t-status-pill" :style="{background: t.project_color, color: '#fff', fontSize:'10px'}">{{ t.project_name }}</span>
								<span class="t-deadline" :class="{late: overdue(t)}">
									<template v-if="t.deadline">📅 {{ formatDate(t.deadline) }}</template>
								</span>
								<span v-if="+t.comments_count">💬 {{ t.comments_count }}</span>
							</div>
						</div>
						<div v-if="!grouped[col.key]?.length" style="text-align:center; color:var(--t-text-3); padding:14px 8px; font-size:11px;">—</div>
					</div>
				</div>
			</div>

			<div>
				<h3 style="margin:0 0 12px; font-size:18px;">פעילות אחרונה</h3>
				<div v-if="!activity.length" class="t-empty" style="padding:30px 16px;">אין פעילות עדיין</div>
				<div v-else class="t-activity-feed">
					<div v-for="a in activity" :key="a.id" class="t-act">
						<div class="t-avatar sm" :style="{background: a.user_color || '#6366f1'}">{{ initials(a.user_name) }}</div>
						<div class="t-act-text">
							<b>{{ a.user_name }}</b>
							{{ actionText(a.action) }}
							<i style="color:var(--t-text-2);">"{{ a.task_title }}"</i>
							<div class="t-act-time">{{ formatDateTime(a.created_at) }}</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<TaskModal v-if="openedTaskId" :task-id="openedTaskId" :user="user" :users="users" :projects-list="projectsList" @close="openedTaskId = 0" @saved="refresh" />
		<TaskModal v-if="newTaskOpen" :task-id="0" :user="user" :users="users" :projects-list="projectsList" @close="newTaskOpen = false" @saved="refresh" />
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials, formatDate, formatDateTime, isOverdue } from "./TasksApi";
import TaskModal from "./TaskModal.vue";

export default {
	name: "TasksDashboard",
	components: { TaskModal },
	props: { user: Object, users: Array, openTaskId: { type: Number, default: 0 }, projectsList: { type: Array, default: () => [] } },
	emits: ["task-opened"],
	data() {
		return {
			stats: {},
			myTasks: [],
			activity: [],
			openedTaskId: 0,
			newTaskOpen: false,
			dragId: null,
			subscription: null,
			columns: [
				{ key: "todo",        label: "להתחלה" },
				{ key: "in_progress", label: "בעבודה" },
				{ key: "review",      label: "בבדיקה" },
				{ key: "done",        label: "הושלם" },
			],
		};
	},
	computed: {
		grouped() {
			const g = {};
			this.columns.forEach(c => g[c.key] = []);
			for (const t of this.myTasks) {
				if (!g[t.status]) g[t.status] = [];
				g[t.status].push(t);
			}
			return g;
		},
	},
	async mounted() {
		this._refreshDeb = (() => {
			let h;
			return () => { clearTimeout(h); h = setTimeout(() => this.refresh(), 600); };
		})();
		const sub = TasksRealtime.subscribe("tasks-global", {
			"task.updated": () => this._refreshDeb(),
			"task.created": () => this._refreshDeb(),
		});
		const [, subRes] = await Promise.all([this.refresh(), sub]);
		this.subscription = subRes;
		if (this.openTaskId) {
			this.openedTaskId = this.openTaskId;
			this.$emit("task-opened");
		}
	},
	beforeUnmount() {
		if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
	},
	watch: {
		openTaskId(v) {
			if (v) {
				this.openedTaskId = v;
				this.$emit("task-opened");
			}
		},
	},
	methods: {
		initials, formatDate, formatDateTime,
		overdue(t) { return isOverdue(t.deadline, t.status); },
		priorityLabel(p) { return { low: "נמוכה", medium: "בינונית", high: "גבוהה", urgent: "דחוף" }[p] || p; },
		async refresh() {
			const res = await TasksAPI.dashboard();
			this.stats    = res?.stats    || {};
			this.myTasks  = res?.my_tasks || [];
			this.activity = res?.activity || [];
		},
		openTask(id) { this.openedTaskId = id; },
		async toggleDone(t) {
			const next = t.status === "done" ? "todo" : "done";
			const prev = t.status;
			t.status = next; // optimistic — recomputes grouped
			const res = await TasksAPI.taskStatus(t.id, next);
			if (res && res.error) { t.status = prev; return; }
			if (this._refreshDeb) this._refreshDeb(); // refresh stat counters
		},
		dragStart(t, e) { this.dragId = t.id; e.dataTransfer.effectAllowed = "move"; },
		async dropTo(status, e) {
			e.preventDefault();
			if (!this.dragId) return;
			const t = this.myTasks.find(x => x.id == this.dragId);
			if (t && t.status !== status) {
				t.status = status;
				await TasksAPI.taskStatus(this.dragId, status);
			}
			this.dragId = null;
		},
		actionText(a) {
			return ({
				created: "יצר את",
				status_changed: "עדכן סטטוס של",
				assigned: "שייך את",
				commented: "הגיב על",
			}[a] || "עדכן את");
		},
	},
};
</script>
